import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useSupabase } from '../contexts/SupabaseContext';
import AdminNavBar from '../components/AdminNavBar';
import { useAuth } from '../hooks/useAuth';
import { Submission, Document, DecisionType, ReviewDecisionInsert } from '../lib/supabaseClient';
import FeedbackAndResponses from '../components/FeedbackAndResponses';
import { ReviewCycle } from '../types/submission';
import withIdleTimer from '../hoc/withIdleTimer';

// Helper function to determine the bucket name based on document type
const getDocumentBucket = (docType: string): string => {
  return ['submission_form_markdown', 'submission_form_pdf','decision_letter'].includes(docType)
    ? 'tempus-created-docs'
    : 'user-uploaded-docs';
};

const AdminReview: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user, signOut, loading: authLoading, sessionExpired } = useAuth();
  
  // Memoize the Supabase client
  const supabase = useSupabase();
  
  const [submission, setSubmission] = useState<Submission | null>(null);
  const [documents, setDocuments] = useState<Document[]>([]);
  const [inactiveDocuments, setInactiveDocuments] = useState<Document[]>([]);
  const [documentUrls, setDocumentUrls] = useState<Record<string, string>>({});
  const [inactiveDocumentUrls, setInactiveDocumentUrls] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [updateStatus, setUpdateStatus] = useState<'idle' | 'updating' | 'success' | 'error'>('idle');
  
  // Add refs to track state and prevent duplicate operations
  const isFetchingRef = useRef(false);
  const userIdRef = useRef<string | null>(null);
  
  // Editable fields state
  const [classification, setClassification] = useState<string>('');
  const [status, setStatus] = useState<string>('');
  const [readyForPiApproval, setReadyForPiApproval] = useState<boolean>(false);
  const [piApprovalStatusMsg, setPiApprovalStatusMsg] = useState<string>('');
  const [piApprovalMsgStatus, setPiApprovalMsgStatus] = useState<'idle' | 'updating' | 'success' | 'error'>('idle');
  const [paymentCompleted, setPaymentCompleted] = useState<boolean>(false);
  const [piApproval, setPiApproval] = useState<boolean>(false);

  // Review data state
  const [cycle, setCycle] = useState<string>('');
  const [reviewSubmitStatus, setReviewSubmitStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  
  // New state for feedback items
  const [feedbackItems, setFeedbackItems] = useState<Array<{type: string, text: string}>>([
    { type: 'required_change', text: '' }
  ]);

  // Decision data state
  const [decision, setDecision] = useState<string>('');
  const [decisionData, setDecisionData] = useState<string>('');
  const [decisionSubmitStatus, setDecisionSubmitStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');

  // Add decision letter file state
  const [decisionFile, setDecisionFile] = useState<File | null>(null);
  const [decisionFileError, setDecisionFileError] = useState<string | null>(null);
  const [fileUploading, setFileUploading] = useState(false);

  // Handle sign out
  const handleSignOut = async () => {
    try {
      await signOut();
      navigate('/signin');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  // Add check for auth loading and session expiration
  useEffect(() => {
    // If session is expired, redirect to auth page
    if (sessionExpired) {
      navigate('/signin');
    }
  }, [sessionExpired, navigate]);

  // Memoize the fetchSubmissionAndDocuments function
  const fetchSubmissionAndDocuments = useCallback(async () => {
    // Skip if already fetching or not authenticated
    if (isFetchingRef.current || !user?.id || !id) {
      return;
    }
    
    isFetchingRef.current = true;
    setLoading(true);
    setError(null);

    try {
      // Fetch submission with authenticated client
      const { data: submissionData, error: submissionError } = await supabase
        .from('submissions')
        .select('*')
        .eq('id', id)
        .single();

      if (submissionError) {
        setError(`Failed to fetch submission: ${submissionError.message}`);
        return;
      }

      if (!submissionData) {
        setError('Submission not found');
        return;
      }

      setSubmission(submissionData as unknown as Submission);
      setClassification(submissionData.classification || '');
      setStatus(submissionData.status || '');
      setReadyForPiApproval(!!submissionData.ready_for_pi_approval);
      setPiApprovalStatusMsg(submissionData.pi_approval_status_msg || '');
      setPaymentCompleted(!!submissionData.payment_completed);
      setPiApproval(!!submissionData.pi_approval);

      // Fetch active documents
      const { data: activeDocumentsData, error: activeDocumentsError } = await supabase
        .from('documents')
        .select('*')
        .eq('submission_id', id)
        .eq('is_active', true);

      if (activeDocumentsError) {
        console.error('Error fetching active documents:', activeDocumentsError);
      }

      setDocuments(activeDocumentsData as Document[] || []);

      // Fetch inactive documents
      const { data: inactiveDocumentsData, error: inactiveDocumentsError } = await supabase
        .from('documents')
        .select('*')
        .eq('submission_id', id)
        .eq('is_active', false);

      if (inactiveDocumentsError) {
        console.error('Error fetching inactive documents:', inactiveDocumentsError);
      }

      setInactiveDocuments(inactiveDocumentsData as Document[] || []);
    } catch (err: any) {
      setError(`Unexpected error: ${err.message}`);
    } finally {
      isFetchingRef.current = false;
      setLoading(false);
    }
  }, [id, supabase, user?.id]);

  // Fetch submission only after auth is confirmed
  useEffect(() => {
    // Update userIdRef if user changes
    if (user?.id !== userIdRef.current) {
      userIdRef.current = user?.id || null;
    }
    
    // Skip fetching if auth is still loading
    if (authLoading) {
      return;
    }

    fetchSubmissionAndDocuments();
  }, [authLoading, fetchSubmissionAndDocuments]);

  // Generate signed URLs for active documents
  useEffect(() => {
    const generateSignedUrls = async () => {
      if (!documents.length || !user) return;
      
      const urls: Record<string, string> = {};
      
      for (const doc of documents) {
        // Determine which bucket to use based on document type
        const bucketName = getDocumentBucket(doc.type || '');
        
        const { data, error } = await supabase.storage
          .from(bucketName)
          .createSignedUrl(doc.file_path, 3600);
        
        if (data && !error) {
          urls[doc.id] = data.signedUrl;
        } else {
          console.error(`Error generating signed URL from ${bucketName}:`, error);
        }
      }
      
      setDocumentUrls(urls);
    };
    
    generateSignedUrls();
  }, [documents, user, supabase]);

  // Generate signed URLs for inactive documents
  useEffect(() => {
    const generateInactiveSignedUrls = async () => {
      if (!inactiveDocuments.length || !user) return;
      
      const urls: Record<string, string> = {};
      
      for (const doc of inactiveDocuments) {
        // Determine which bucket to use based on document type
        const bucketName = getDocumentBucket(doc.type || '');
        
        const { data, error } = await supabase.storage
          .from(bucketName)
          .createSignedUrl(doc.file_path, 3600);
        
        if (data && !error) {
          urls[doc.id] = data.signedUrl;
        } else {
          console.error(`Error generating signed URL for inactive document from ${bucketName}:`, error);
        }
      }
      
      setInactiveDocumentUrls(urls);
    };
    
    generateInactiveSignedUrls();
  }, [inactiveDocuments, user, supabase]);

  // Update submission field with authenticated client
  const updateSubmissionField = async (field: string, value: string | boolean) => {
    if (!submission) return;
    if (!user) {
      console.error('No authenticated user found');
      setUpdateStatus('error');
      return;
    }
    
    setUpdateStatus('updating');
    
    try {
      // Use authenticated client to update submission
      const { data, error } = await supabase
        .from('submissions')
        .update({ 
          [field]: value, 
          updated_at: new Date().toISOString() 
        })
        .eq('id', submission.id)
        .select();
      
      if (error) {
        console.error(`Error updating ${field}:`, error);
        setUpdateStatus('error');
        return;
      }

      if (!data || data.length === 0) {
        console.error('No rows were updated');
        setUpdateStatus('error');
        return;
      }
      
      setSubmission({
        ...submission,
        [field]: value,
        updated_at: new Date().toISOString()
      });
      
      setUpdateStatus('success');
      
      // Set timeout to clear success status
      setTimeout(() => {
        setUpdateStatus('idle');
      }, 3000);
      
    } catch (err) {
      console.error(`Error updating ${field}:`, err);
      setUpdateStatus('error');
      
      // Set timeout to clear error status
      setTimeout(() => {
        setUpdateStatus('idle');
      }, 3000);
    }
  };

  // New function to add feedback item
  const addFeedbackItem = () => {
    if (feedbackItems.length < 20) {
      setFeedbackItems([...feedbackItems, { type: 'required_change', text: '' }]);
    }
  };

  // New function to remove feedback item
  const removeFeedbackItem = (index: number) => {
    if (feedbackItems.length > 1) {
      const newItems = [...feedbackItems];
      newItems.splice(index, 1);
      setFeedbackItems(newItems);
    }
  };

  // New function to update feedback item
  const updateFeedbackItem = (index: number, field: 'type' | 'text', value: string) => {
    const newItems = [...feedbackItems];
    newItems[index] = { ...newItems[index], [field]: value };
    setFeedbackItems(newItems);
  };

  // Modified submit review function
  const handleReviewSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!submission) return;
    if (!user) {
      console.error('No authenticated user found');
      setReviewSubmitStatus('error');
      return;
    }
    
    // Input validation
    const cycleNumber = parseInt(cycle, 10);
    if (isNaN(cycleNumber) || cycleNumber <= 0) {
      alert('Please enter a valid positive number for the cycle');
      return;
    }
    
    // Validate all feedback items have text with minimum length
    const invalidItems = feedbackItems.filter(item => item.text.trim().length < 10);
    if (invalidItems.length > 0) {
      alert('All feedback items must have at least 10 characters');
      return;
    }
    
    // Ensure at least one feedback item exists
    if (feedbackItems.length === 0) {
      alert('Please add at least one feedback item');
      return;
    }
    
    setReviewSubmitStatus('submitting');
    
    try {
      // Group feedback items by type
      const groupedFeedback = feedbackItems.reduce((acc, item) => {
        if (!acc[item.type]) {
          acc[item.type] = [];
        }
        acc[item.type].push(item.text.trim());
        return acc;
      }, {} as Record<string, string[]>);
      
      // Transform to required structure
      const formattedFeedback = Object.entries(groupedFeedback).map(([type, items]) => ({
        type,
        feedback_items: items
      }));
      
      // Prepare the new review cycle entry
      const newEntry = {
        cycle: cycleNumber,
        feedback: formattedFeedback,
        'feedback-timestamp': new Date().toISOString()
      };
      
      // Get current review data or create initial structure
      let currentReviewData = submission.review_data || { review_cycles: [] };
      
      // Ensure currentReviewData is an object with review_cycles property
      if (typeof currentReviewData !== 'object' || !currentReviewData) {
        currentReviewData = { review_cycles: [] };
      }
      
      // Add new entry to review cycles array
      const updatedReviewData = {
        review_cycles: [...(currentReviewData as any).review_cycles, newEntry]
      };
      
      // Update in database
      const { data, error } = await supabase
        .from('submissions')
        .update({ 
          review_data: updatedReviewData,
          updated_at: new Date().toISOString() 
        })
        .eq('id', submission.id)
        .select();
      
      if (error) {
        console.error('Error updating review data:', error);
        setReviewSubmitStatus('error');
        
        // Set timeout to clear error status
        setTimeout(() => {
          setReviewSubmitStatus('idle');
        }, 3000);
        
        return;
      }
      
      // Update local state
      setSubmission({
        ...submission,
        review_data: updatedReviewData,
        updated_at: new Date().toISOString()
      });
      
      // Reset form
      setCycle('');
      setFeedbackItems([{ type: 'required_change', text: '' }]);
      setReviewSubmitStatus('success');
      
      // Set timeout to clear success status
      setTimeout(() => {
        setReviewSubmitStatus('idle');
      }, 3000);
      
    } catch (err) {
      console.error('Error submitting review data:', err);
      setReviewSubmitStatus('error');
      
      // Set timeout to clear error status
      setTimeout(() => {
        setReviewSubmitStatus('idle');
      }, 3000);
    }
  };

  // Handle decision file changes
  const handleDecisionFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setDecisionFileError(null);
    
    const files = e.target.files;
    if (!files || files.length === 0) {
      setDecisionFile(null);
      return;
    }
    
    const file = files[0];
    
    // Validate file type
    if (file.type !== 'application/pdf') {
      setDecisionFileError('Only PDF files are allowed');
      return;
    }
    
    // Validate file size (10MB max)
    if (file.size > 10 * 1024 * 1024) {
      setDecisionFileError('File size must be less than 10MB');
      return;
    }
    
    setDecisionFile(file);
  };

  // Remove decision file
  const handleRemoveDecisionFile = () => {
    setDecisionFile(null);
    setDecisionFileError(null);
    
    // Reset file input
    const fileInput = document.getElementById('decisionFile') as HTMLInputElement;
    if (fileInput) {
      fileInput.value = '';
    }
  };

  // Format date for filename (DDMMMYYYY)
  const formatDateForFilename = (date: Date) => {
    const day = date.getDate().toString().padStart(2, '0');
    const month = date.toLocaleString('en-US', { month: 'short' }).toUpperCase();
    const year = date.getFullYear();
    return `${day}${month}${year}`;
  };

  // Modified handleDecisionSubmit to include file upload
  const handleDecisionSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!submission) return;
    if (!user) {
      console.error('No authenticated user found');
      setDecisionSubmitStatus('error');
      return;
    }
    
    // Input validation
    if (!decision) {
      alert('Please select a decision');
      return;
    }
    
    if (decisionData.trim().length < 500) {
      alert('Decision data must be at least 500 characters');
      return;
    }
    
    // File validation
    if (!decisionFile) {
      setDecisionFileError('Please upload a decision letter');
      return;
    }
    
    setDecisionSubmitStatus('submitting');
    setFileUploading(true);
    
    try {
      // Create a new UUID for the decision record
      const decisionId = crypto.randomUUID();
      
      // Create new decision record in review_decisions table
      const { error: decisionError } = await supabase
        .from('review_decisions')
        .insert({ 
          id: decisionId,
          submission_id: submission.id,
          reviewer_id: user.id,
          decision: decision as DecisionType,
          decision_data: decisionData.trim(),
          created_at: new Date().toISOString() 
        } as ReviewDecisionInsert);
      
      if (decisionError) {
        console.error('Error creating decision record:', decisionError);
        setDecisionSubmitStatus('error');
        setFileUploading(false);
        setTimeout(() => {
          setDecisionSubmitStatus('idle');
        }, 3000);
        return;
      }
      
      // Create custom filename for the decision letter
      const today = new Date();
      const formattedDate = formatDateForFilename(today);
      const refName = submission.data && typeof submission.data === 'object' && 'ref_name' in submission.data
        ? String(submission.data.ref_name) 
        : 'NO_REF';
      const customFilename = `${refName}_Tempus IRB decision letter_${formattedDate}.pdf`;
      
      // Path in storage bucket (folder = submission ID)
      const filePath = `${submission.id}/${customFilename}`;
      
      // Upload file to Supabase storage
      const { data: fileData, error: fileUploadError } = await supabase.storage
        .from('tempus-created-docs')
        .upload(filePath, decisionFile, {
          cacheControl: '3600',
          upsert: false
        });
      
      if (fileUploadError) {
        console.error('Error uploading decision letter:', fileUploadError);
        setDecisionSubmitStatus('error');
        setFileUploading(false);
        setTimeout(() => {
          setDecisionSubmitStatus('idle');
        }, 3000);
        return;
      }
      
      // Create document record in documents table
      const documentId = crypto.randomUUID();
      const { error: documentError } = await supabase
        .from('documents')
        .insert({
          id: documentId,
          submission_id: submission.id,
          user_id: null,
          filename: customFilename,
          file_path: filePath,
          uploaded_at: new Date().toISOString(),
          type: 'decision_letter',
          is_active: true
        });
      
      if (documentError) {
        console.error('Error creating document record:', documentError);
        setDecisionSubmitStatus('error');
        setFileUploading(false);
        setTimeout(() => {
          setDecisionSubmitStatus('idle');
        }, 3000);
        return;
      }
      
      // Update submission with decision_id and change status to DECISION_ISSUED
      const { data, error: submissionError } = await supabase
        .from('submissions')
        .update({ 
          decision_id: decisionId,
          status: 'DECISION_ISSUED',
          updated_at: new Date().toISOString() 
        })
        .eq('id', submission.id)
        .select();
      
      if (submissionError) {
        console.error('Error updating submission with decision:', submissionError);
        setDecisionSubmitStatus('error');
        setFileUploading(false);
        setTimeout(() => {
          setDecisionSubmitStatus('idle');
        }, 3000);
        return;
      }
      
      // Update local state
      setSubmission({
        ...submission,
        decision_id: decisionId,
        status: 'DECISION_ISSUED',
        updated_at: new Date().toISOString()
      });
      
      // Update status in UI state to match
      setStatus('DECISION_ISSUED');
      
      // Reset form
      setDecision('');
      setDecisionData('');
      setDecisionFile(null);
      setDecisionSubmitStatus('success');
      setFileUploading(false);
      
      setTimeout(() => {
        setDecisionSubmitStatus('idle');
      }, 3000);
      
    } catch (err) {
      console.error('Error submitting decision data:', err);
      setDecisionSubmitStatus('error');
      setFileUploading(false);
      
      setTimeout(() => {
        setDecisionSubmitStatus('idle');
      }, 3000);
    }
  };

  // Add new function to update PI approval status message
  const updatePiApprovalStatusMsg = async () => {
    if (!submission) return;
    if (!user) {
      console.error('No authenticated user found');
      setPiApprovalMsgStatus('error');
      return;
    }
    
    setPiApprovalMsgStatus('updating');
    
    try {
      // Use authenticated client to update submission
      const { data, error } = await supabase
        .from('submissions')
        .update({ 
          pi_approval_status_msg: piApprovalStatusMsg, 
          updated_at: new Date().toISOString() 
        })
        .eq('id', submission.id)
        .select();
      
      if (error) {
        console.error(`Error updating PI approval status message:`, error);
        setPiApprovalMsgStatus('error');
        return;
      }

      if (!data || data.length === 0) {
        console.error('No rows were updated');
        setPiApprovalMsgStatus('error');
        return;
      }
      
      setSubmission({
        ...submission,
        pi_approval_status_msg: piApprovalStatusMsg,
        updated_at: new Date().toISOString()
      });
      
      setPiApprovalMsgStatus('success');
      
      // Set timeout to clear success status
      setTimeout(() => {
        setPiApprovalMsgStatus('idle');
      }, 3000);
      
    } catch (err) {
      console.error(`Error updating PI approval status message:`, err);
      setPiApprovalMsgStatus('error');
      
      // Set timeout to clear error status
      setTimeout(() => {
        setPiApprovalMsgStatus('idle');
      }, 3000);
    }
  };

  // Show loading spinner during auth loading
  if (authLoading) {
    return (
      <>
        <AdminNavBar user={user} onSignOut={handleSignOut} />
        <div className="p-8 mt-16">
          <h1 className="text-xl font-bold mb-4">Loading...</h1>
          <div className="flex items-center justify-start">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#F55D3E]"></div>
            <span className="ml-2 text-gray-600">Verifying your account</span>
          </div>
        </div>
      </>
    );
  }

  if (loading) {
    return (
      <>
        <AdminNavBar user={user} onSignOut={handleSignOut} />
        <div className="p-8 mt-16">
          <h1 className="text-xl font-bold mb-4">Loading submission...</h1>
          <div className="flex items-center justify-start">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#F55D3E]"></div>
            <span className="ml-2 text-gray-600">Please wait</span>
          </div>
        </div>
      </>
    );
  }

  if (error) {
    return (
      <>
        <AdminNavBar user={user} onSignOut={handleSignOut} />
        <div className="p-8 mt-16">
          <h1 className="text-xl font-bold mb-4">Error</h1>
          <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-6">
            <p className="text-red-700">{error}</p>
          </div>
          <button
            onClick={() => navigate('/admin')}
            className="px-4 py-2 bg-[#F55D3E] text-white rounded hover:bg-[#e04d30] transition-colors"
          >
            Back to Dashboard
          </button>
        </div>
      </>
    );
  }

  if (!submission) {
    return (
      <>
        <AdminNavBar user={user} onSignOut={handleSignOut} />
        <div className="p-8 mt-16">
          <h1 className="text-xl font-bold mb-4">Submission Not Found</h1>
          <p className="text-gray-600 mb-6">
            The submission either does not exist or you do not have permission to view it.
          </p>
          <button
            onClick={() => navigate('/admin')}
            className="px-4 py-2 bg-[#F55D3E] text-white rounded hover:bg-[#e04d30] transition-colors"
          >
            Back to Dashboard
          </button>
        </div>
      </>
    );
  }

  return (
    <>
      <AdminNavBar user={user} onSignOut={handleSignOut} />
      <div className="p-8 mt-16 max-w-7xl mx-auto">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h2 className="text-2xl font-bold text-[#343a40]">
              {submission.data && typeof submission.data === 'object' && 'protocol_title' in submission.data
                ? String(submission.data.protocol_title)
                : 'Submission Review'}
            </h2>
            <h3 className="text-xl text-[#343a40] mt-1">
              Sponsor: {submission.data && typeof submission.data === 'object' && 'sponsor_name' in submission.data
                ? String(submission.data.sponsor_name)
                : ''}
            </h3>
            <h4 className="text-lg text-gray-600">
              PI: {submission.data && typeof submission.data === 'object' && 'pi_first_name' in submission.data
                ? String(submission.data.pi_first_name)
                : ''} {submission.data && typeof submission.data === 'object' && 'pi_last_name' in submission.data
                ? String(submission.data.pi_last_name)
                : ''}
            </h4>
            <h4 className="text-lg text-gray-600">
              PI Email: {submission.data && typeof submission.data === 'object' && 'pi_email' in submission.data
                ? String(submission.data.pi_email)
                : ''}
            </h4>
            <h4 className="text-lg text-gray-600">
              Billing Email: {submission.data && typeof submission.data === 'object' && 'invoice_email' in submission.data
                ? String(submission.data.invoice_email)
                : ''}
            </h4>
          </div>
        </div>
        
        {/* Update status notification */}
        {updateStatus === 'success' && (
          <div className="bg-green-50 border-l-4 border-green-500 p-4 mb-6 animate-fadeOut">
            <p className="text-green-700">Update successful</p>
          </div>
        )}
        
        {updateStatus === 'error' && (
          <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-6">
            <p className="text-red-700">Update failed. Please try again.</p>
          </div>
        )}
        
        {/* Submission Info Card */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-8">
          <h2 className="text-xl font-semibold text-[#343a40] mb-6 pb-2 border-b border-gray-200">
            Submission Information
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Basic info - not editable */}
            <div>
              <div className="mb-4">
                <p className="text-sm text-gray-500">Submission ID</p>
                <p className="font-mono">{submission.id}</p>
              </div>
              
              <div className="mb-4">
                <p className="text-sm text-gray-500">User ID</p>
                <p className="font-mono">{submission.user_id}</p>
              </div>
              
              <div className="mb-4">
                <p className="text-sm text-gray-500">Decision ID</p>
                <p className="font-mono">{submission.decision_id || 'N/A'}</p>
              </div>
              
              <div className="mb-4">
                <p className="text-sm text-gray-500">Created At</p>
                <p>{new Date(submission.created_at).toLocaleString()}</p>
              </div>
              
              <div className="mb-4">
                <p className="text-sm text-gray-500">Last Updated</p>
                <p>{new Date(submission.updated_at).toLocaleString()}</p>
              </div>
            </div>
            
            {/* Editable Fields */}
            <div>
              {/* Classification */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Classification
                </label>
                <div className="flex items-center space-x-2">
                  <select
                    value={classification}
                    onChange={(e) => setClassification(e.target.value)}
                    className="border-gray-300 rounded-md shadow-sm focus:border-[#F55D3E] focus:ring focus:ring-[#F55D3E] focus:ring-opacity-50 w-full"
                  >
                    <option value="">-- Select Classification --</option>
                    <option value="EXEMPT">Exempt</option>
                    <option value="EXPEDITED">Expedited</option>
                    <option value="FULL_BOARD">Full Board</option>
                  </select>
                  <button
                    onClick={() => updateSubmissionField('classification', classification)}
                    disabled={classification === submission.classification}
                    className={`px-3 py-1 rounded text-sm ${
                      classification === submission.classification
                        ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                        : 'bg-blue-500 text-white hover:bg-blue-600'
                    }`}
                  >
                    Save
                  </button>
                </div>
              </div>
              
              {/* Status */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Status
                </label>
                <div className="flex items-center space-x-2">
                  <select
                    value={status}
                    onChange={(e) => setStatus(e.target.value)}
                    className="border-gray-300 rounded-md shadow-sm focus:border-[#F55D3E] focus:ring focus:ring-[#F55D3E] focus:ring-opacity-50 w-full"
                  >
                    <option value="">-- Select Status --</option>
                    <option value="DRAFT">Draft</option>
                    <option value="IRB_REVIEW">IRB Review</option>
                    <option value="NEEDS_ACTION">Needs Action</option>
                    <option value="DECISION_ISSUED">Decision Issued</option>
                  </select>
                  <button
                    onClick={() => updateSubmissionField('status', status)}
                    disabled={status === submission.status}
                    className={`px-3 py-1 rounded text-sm ${
                      status === submission.status
                        ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                        : 'bg-blue-500 text-white hover:bg-blue-600'
                    }`}
                  >
                    Save
                  </button>
                </div>
              </div>
              
              {/* Ready for PI Approval */}
              <div className="mb-4">
                <div className="flex items-center justify-between">
                  <label className="block text-sm font-medium text-gray-700">
                    Ready for PI Approval?
                  </label>
                  <div className="flex items-center space-x-2">
                    <label className="inline-flex items-center">
                      <input
                        type="checkbox"
                        checked={readyForPiApproval}
                        onChange={(e) => setReadyForPiApproval(e.target.checked)}
                        className="rounded text-[#F55D3E] focus:ring-[#F55D3E]"
                      />
                      <span className="ml-2 text-sm text-gray-700">
                        {readyForPiApproval ? 'Yes' : 'No'}
                      </span>
                    </label>
                    <button
                      onClick={() => updateSubmissionField('ready_for_pi_approval', readyForPiApproval)}
                      disabled={readyForPiApproval === submission.ready_for_pi_approval}
                      className={`px-3 py-1 rounded text-sm ${
                        readyForPiApproval === submission.ready_for_pi_approval
                          ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                          : 'bg-blue-500 text-white hover:bg-blue-600'
                      }`}
                    >
                      Save
                    </button>
                  </div>
                </div>
              </div>
              
              {/* PI Approval Status Message */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  PI Approval Status Message
                </label>
                <div className="flex items-center space-x-2">
                  <input
                    type="text"
                    value={piApprovalStatusMsg}
                    onChange={(e) => setPiApprovalStatusMsg(e.target.value)}
                    className="border-gray-300 rounded-md shadow-sm focus:border-[#F55D3E] focus:ring focus:ring-[#F55D3E] focus:ring-opacity-50 w-full"
                    placeholder="Enter status message for PI approval"
                  />
                  <button
                    onClick={updatePiApprovalStatusMsg}
                    disabled={piApprovalMsgStatus === 'updating'}
                    className={`px-3 py-1 rounded text-sm ${
                      piApprovalMsgStatus === 'updating'
                        ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                        : 'bg-blue-500 text-white hover:bg-blue-600'
                    }`}
                  >
                    {piApprovalMsgStatus === 'updating' ? (
                      <span className="flex items-center">
                        <svg className="animate-spin -ml-1 mr-1 h-3 w-3 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Saving...
                      </span>
                    ) : (
                      'Save'
                    )}
                  </button>
                </div>
                {piApprovalMsgStatus === 'success' && (
                  <p className="mt-1 text-xs text-green-600">Status message saved successfully</p>
                )}
                {piApprovalMsgStatus === 'error' && (
                  <p className="mt-1 text-xs text-red-600">Failed to save status message</p>
                )}
              </div>
              
              {/* Payment Completed */}
              <div className="mb-4">
                <div className="flex items-center justify-between">
                  <label className="block text-sm font-medium text-gray-700">
                    Payment Completed
                  </label>
                  <div className="flex items-center space-x-2">
                    <label className="inline-flex items-center">
                      <input
                        type="checkbox"
                        checked={paymentCompleted}
                        onChange={(e) => setPaymentCompleted(e.target.checked)}
                        className="rounded text-[#F55D3E] focus:ring-[#F55D3E]"
                      />
                      <span className="ml-2 text-sm text-gray-700">
                        {paymentCompleted ? 'Yes' : 'No'}
                      </span>
                    </label>
                    <button
                      onClick={() => updateSubmissionField('payment_completed', paymentCompleted)}
                      disabled={paymentCompleted === submission.payment_completed}
                      className={`px-3 py-1 rounded text-sm ${
                        paymentCompleted === submission.payment_completed
                          ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                          : 'bg-blue-500 text-white hover:bg-blue-600'
                      }`}
                    >
                      Save
                    </button>
                  </div>
                </div>
              </div>
              
              {/* PI Approval */}
              <div className="mb-4">
                <div className="flex items-center justify-between">
                  <label className="block text-sm font-medium text-gray-700">
                    PI Approval Completed?
                  </label>
                  <div className="flex items-center space-x-2">
                    <label className="inline-flex items-center">
                      <input
                        type="checkbox"
                        checked={piApproval}
                        onChange={(e) => setPiApproval(e.target.checked)}
                        className="rounded text-[#F55D3E] focus:ring-[#F55D3E]"
                      />
                      <span className="ml-2 text-sm text-gray-700">
                        {piApproval ? 'Yes' : 'No'}
                      </span>
                    </label>
                    <button
                      onClick={() => updateSubmissionField('pi_approval', piApproval)}
                      disabled={piApproval === submission.pi_approval}
                      className={`px-3 py-1 rounded text-sm ${
                        piApproval === submission.pi_approval
                          ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                          : 'bg-blue-500 text-white hover:bg-blue-600'
                      }`}
                    >
                      Save
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Active Documents Table */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-8">
          <h2 className="text-xl font-semibold text-[#343a40] mb-6 pb-2 border-b border-gray-200">
            Active Submission Documents
          </h2>
          
          {documents.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-gray-500 italic">No active documents found for this submission</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Filename
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      File Path
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Type
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Storage
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Uploaded At
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {documents.map((doc) => {
                    const bucketName = getDocumentBucket(doc.type || '');
                    const isTempusCreated = bucketName === 'tempus-created-docs';
                    
                    return (
                      <tr key={doc.id}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {doc.filename}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          {documentUrls[doc.id] ? (
                            <button 
                              onClick={() => {
                                // Force download by converting to blob
                                fetch(documentUrls[doc.id])
                                  .then(response => response.blob())
                                  .then(blob => {
                                    // Create blob URL and trigger download
                                    const blobUrl = window.URL.createObjectURL(blob);
                                    const link = document.createElement('a');
                                    link.href = blobUrl;
                                    link.download = doc.filename;
                                    document.body.appendChild(link);
                                    link.click();
                                    // Clean up
                                    window.URL.revokeObjectURL(blobUrl);
                                    document.body.removeChild(link);
                                  })
                                  .catch(err => {
                                    console.error("Download failed:", err);
                                    alert("Download failed. Please try again.");
                                  });
                              }}
                              className="inline-flex items-center px-3 py-1 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-[#F55D3E] hover:bg-[#e04d30] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#F55D3E]"
                            >
                              <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                              </svg>
                              Download File
                            </button>
                          ) : (
                            <span className="flex items-center text-gray-400">
                              <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                              </svg>
                              Loading file...
                            </span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {doc.type}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          {isTempusCreated ? (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                              Tempus created
                            </span>
                          ) : (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                              User uploaded
                            </span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {new Date(doc.uploaded_at).toLocaleString()}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
        
        {/* Inactive Documents Table */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-semibold text-[#343a40] mb-6 pb-2 border-b border-gray-200">
            Inactive Submission Documents
          </h2>
          
          {inactiveDocuments.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-gray-500 italic">No inactive documents found for this submission</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Filename
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      File Path
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Type
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Storage
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Uploaded At
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {inactiveDocuments.map((doc) => {
                    const bucketName = getDocumentBucket(doc.type || '');
                    const isTempusCreated = bucketName === 'tempus-created-docs';
                    
                    return (
                      <tr key={doc.id} className="bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                          {doc.filename}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          {inactiveDocumentUrls[doc.id] ? (
                            <button 
                              onClick={() => {
                                // Force download by converting to blob
                                fetch(inactiveDocumentUrls[doc.id])
                                  .then(response => response.blob())
                                  .then(blob => {
                                    // Create blob URL and trigger download
                                    const blobUrl = window.URL.createObjectURL(blob);
                                    const link = document.createElement('a');
                                    link.href = blobUrl;
                                    link.download = doc.filename;
                                    document.body.appendChild(link);
                                    link.click();
                                    // Clean up
                                    window.URL.revokeObjectURL(blobUrl);
                                    document.body.removeChild(link);
                                  })
                                  .catch(err => {
                                    console.error("Download failed:", err);
                                    alert("Download failed. Please try again.");
                                  });
                              }}
                              className="inline-flex items-center px-3 py-1 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-gray-500 hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                            >
                              <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                              </svg>
                              Download File
                            </button>
                          ) : (
                            <span className="flex items-center text-gray-400">
                              <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                              </svg>
                              Loading file...
                            </span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                          {doc.type}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          {isTempusCreated ? (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 opacity-75">
                              Tempus created
                            </span>
                          ) : (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 opacity-75">
                              User uploaded
                            </span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                          {new Date(doc.uploaded_at).toLocaleString()}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Submission Data */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-8">
          <h2 className="text-xl font-semibold text-[#343a40] mb-6 pb-2 border-b border-gray-200">
            Submission JSON Data (Active)
          </h2>
          
          <div className="bg-gray-50 p-4 rounded-md overflow-auto max-h-96">
            <pre className="text-sm font-mono text-gray-700 whitespace-pre-wrap">
              {submission.data && typeof submission.data === 'object' && Object.keys(submission.data).length === 0 ? (
                <span className="italic text-gray-500">No data available</span>
              ) : (
                JSON.stringify(submission.data, null, 2)
                  .split('\n')
                  .map((line, index) => {
                    // Add syntax highlighting
                    let coloredLine = line;
                    // Highlight keys
                    coloredLine = coloredLine.replace(
                      /"([^"]+)":/g, 
                      '<span class="text-blue-600">"$1"</span>:'
                    );
                    // Highlight string values
                    coloredLine = coloredLine.replace(
                      /: "([^"]*)"/g, 
                      ': <span class="text-green-600">"$1"</span>'
                    );
                    // Highlight boolean/null values
                    coloredLine = coloredLine.replace(
                      /: (true|false|null)/g, 
                      ': <span class="text-purple-600">$1</span>'
                    );
                    // Highlight numbers
                    coloredLine = coloredLine.replace(
                      /: (\d+(\.\d+)?)/g, 
                      ': <span class="text-orange-600">$1</span>'
                    );
                    
                    return (
                      <div 
                        key={index}
                        className="leading-6"
                        dangerouslySetInnerHTML={{ __html: coloredLine }}
                      />
                    );
                  })
              )}
            </pre>
          </div>
        </div>
        
        {/* Review Data Section - Modified to use horizontal rows */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-8">
          <h2 className="text-xl font-semibold text-[#343a40] mb-6 pb-2 border-b border-gray-200">
            Review Data
          </h2>
          
          <div className="flex flex-col space-y-6">
            {/* Top Row - Form */}
            <div className="bg-gray-50 p-4 rounded-md">
              <h3 className="text-lg font-medium text-[#343a40] mb-4">Add Review Feedback</h3>
              
              {reviewSubmitStatus === 'success' && (
                <div className="bg-green-50 border-l-4 border-green-500 p-4 mb-6 animate-fadeOut">
                  <p className="text-green-700">Review data submitted successfully</p>
                </div>
              )}
              
              {reviewSubmitStatus === 'error' && (
                <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-6">
                  <p className="text-red-700">Failed to submit review data. Please try again.</p>
                </div>
              )}
              
              <form onSubmit={handleReviewSubmit}>
                <div className="mb-4">
                  <label htmlFor="cycle" className="block text-sm font-medium text-gray-700 mb-1">
                    Cycle <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    id="cycle"
                    min="1"
                    step="1"
                    value={cycle}
                    onChange={(e) => setCycle(e.target.value)}
                    className="border-gray-300 rounded-md shadow-sm focus:border-[#F55D3E] focus:ring focus:ring-[#F55D3E] focus:ring-opacity-50 w-full max-w-xs"
                    required
                  />
                  <p className="mt-1 text-xs text-gray-500">Enter a positive integer</p>
                </div>
                
                <div className="mb-4">
                  <div className="flex justify-between items-center mb-2">
                    <label className="block text-sm font-medium text-gray-700">
                      Feedback Items <span className="text-red-500">*</span>
                    </label>
                    <button
                      type="button"
                      onClick={addFeedbackItem}
                      disabled={feedbackItems.length >= 20}
                      className={`px-3 py-1 text-sm rounded-md ${
                        feedbackItems.length >= 20
                          ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                          : 'bg-blue-500 text-white hover:bg-blue-600'
                      }`}
                    >
                      Add New Item
                    </button>
                  </div>
                  
                  <div className="space-y-4 max-h-[500px] overflow-y-auto p-2">
                    {feedbackItems.map((item, index) => (
                      <div key={index} className="p-3 border border-gray-200 rounded-md bg-white">
                        <div className="flex justify-between items-start mb-2">
                          <div className="w-1/3 pr-2">
                            <label className="block text-xs font-medium text-gray-500 mb-1">
                              Feedback Type
                            </label>
                            <select
                              value={item.type}
                              onChange={(e) => updateFeedbackItem(index, 'type', e.target.value)}
                              className="border-gray-300 rounded-md shadow-sm focus:border-[#F55D3E] focus:ring focus:ring-[#F55D3E] focus:ring-opacity-50 w-full text-sm"
                              required
                            >
                              <option value="required_change">Required Change</option>
                              <option value="optional_change">Optional Change</option>
                              <option value="note">Note</option>
                            </select>
                          </div>
                          
                          <button
                            type="button"
                            onClick={() => removeFeedbackItem(index)}
                            disabled={feedbackItems.length <= 1}
                            className={`ml-2 px-2 py-1 rounded-md ${
                              feedbackItems.length <= 1
                                ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                                : 'bg-red-100 text-red-600 hover:bg-red-200'
                            }`}
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        </div>
                        
                        <div>
                          <label className="block text-xs font-medium text-gray-500 mb-1">
                            Feedback Text (10-500 characters)
                          </label>
                          <textarea
                            value={item.text}
                            onChange={(e) => updateFeedbackItem(index, 'text', e.target.value)}
                            className="border-gray-300 rounded-md shadow-sm focus:border-[#F55D3E] focus:ring focus:ring-[#F55D3E] focus:ring-opacity-50 w-full text-sm"
                            rows={2}
                            maxLength={500}
                            placeholder="Enter feedback (minimum 10 characters)"
                            required
                          />
                          <p className="mt-1 text-xs text-gray-500">
                            {item.text.length}/500 characters
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                
                <div className="flex justify-end">
                  <button
                    type="submit"
                    disabled={
                      !cycle || 
                      feedbackItems.some(item => item.text.trim().length < 10) || 
                      reviewSubmitStatus === 'submitting'
                    }
                    className={`px-4 py-2 rounded text-white ${
                      !cycle || 
                      feedbackItems.some(item => item.text.trim().length < 10) || 
                      reviewSubmitStatus === 'submitting'
                        ? 'bg-gray-300 cursor-not-allowed'
                        : 'bg-[#F55D3E] hover:bg-[#e04d30] transition-colors'
                    }`}
                  >
                    {reviewSubmitStatus === 'submitting' ? (
                      <span className="flex items-center">
                        <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Submitting...
                      </span>
                    ) : (
                      'Submit Review Data'
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
        
        {/* Feedback and Responses Section */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-8">
          <h2 className="text-xl font-semibold text-[#343a40] mb-6 pb-2 border-b border-gray-200">
            Data from Previous Review Cycles
          </h2>
          
          {submission?.review_data && typeof submission.review_data === 'object' && 
           'review_cycles' in submission.review_data && 
           Array.isArray(submission.review_data.review_cycles) && 
           submission.review_data.review_cycles.length > 0 ? (
            <div className="space-y-8">
              {/* Get all cycles and sort them in descending order */}
              {[...((submission.review_data.review_cycles as unknown) as ReviewCycle[])]
                .sort((a, b) => b.cycle - a.cycle)
                .map((cycle, index, allCycles) => (
                  <div key={`cycle-${cycle.cycle}`}>
                    {/* Cycle header */}
                    <div className="mb-4 bg-gray-100 p-3 rounded-md">
                      <h3 className="font-medium text-gray-800">
                        Review Cycle {cycle.cycle}
                        <span className="ml-2 text-sm text-gray-500">
                          ({new Date(cycle['feedback-timestamp']).toLocaleDateString()})
                        </span>
                      </h3>
                    </div>

                    {/* Feedback component */}
                    <FeedbackAndResponses
                      submissionId={submission.id}
                      cycleNumber={cycle.cycle}
                      mode="view"
                    />

                    {/* Add separator between cycles (except after the last one) */}
                    {index < allCycles.length - 1 && (
                      <div className="border-t border-dashed border-gray-300 my-6 pt-2 flex justify-center">
                        <span className="px-4 py-1 bg-gray-100 text-gray-500 text-sm rounded-full">
                          Previous cycle below
                        </span>
                      </div>
                    )}
                  </div>
                ))}
            </div>
          ) : (
            <div className="bg-gray-50 p-4 rounded-md text-gray-600">
              <p>No feedback cycles are available for this submission.</p>
            </div>
          )}
        </div>
        
        {/* Decision Data Section */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-8">
          <h2 className="text-xl font-semibold text-[#343a40] mb-6 pb-2 border-b border-gray-200">
            Decision Data
          </h2>
          
          {decisionSubmitStatus === 'success' && (
            <div className="bg-green-50 border-l-4 border-green-500 p-4 mb-6 animate-fadeOut">
              <p className="text-green-700">Decision submitted successfully</p>
            </div>
          )}
          
          {decisionSubmitStatus === 'error' && (
            <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-6">
              <p className="text-red-700">Failed to submit decision. Please try again.</p>
            </div>
          )}
          
          <form onSubmit={handleDecisionSubmit}>
            <div className="grid grid-cols-1 gap-6">
              <div className="mb-4">
                <label htmlFor="decision" className="block text-sm font-medium text-gray-700 mb-1">
                  Final Decision <span className="text-red-500">*</span>
                </label>
                <select
                  id="decision"
                  value={decision}
                  onChange={(e) => setDecision(e.target.value)}
                  className="border-gray-300 rounded-md shadow-sm focus:border-[#F55D3E] focus:ring focus:ring-[#F55D3E] focus:ring-opacity-50 w-full"
                  required
                >
                  <option value="">-- Select Decision --</option>
                  <option value="APPROVED">Approved</option>
                  <option value="COND_APPROVED">Conditionally Approved</option>
                  <option value="DENIED">Denied</option>
                </select>
              </div>
              
              <div className="mb-4">
                <label htmlFor="decisionData" className="block text-sm font-medium text-gray-700 mb-1">
                  Decision Data <span className="text-red-500">*</span>
                </label>
                <textarea
                  id="decisionData"
                  rows={6}
                  value={decisionData}
                  onChange={(e) => setDecisionData(e.target.value)}
                  className="border-gray-300 rounded-md shadow-sm focus:border-[#F55D3E] focus:ring focus:ring-[#F55D3E] focus:ring-opacity-50 w-full"
                  placeholder="Enter detailed decision data (minimum 500 characters)"
                  required
                />
                <p className="mt-1 text-xs text-gray-500">
                  {decisionData.length} / 5000 characters (minimum 500)
                </p>
              </div>
              
              {/* New File Upload Input */}
              <div className="mb-4">
                <label htmlFor="decisionFile" className="block text-sm font-medium text-gray-700 mb-1">
                  Upload Decision Letter <span className="text-red-500">*</span>
                </label>
                <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-md">
                  <div className="space-y-1 text-center">
                    {!decisionFile ? (
                      <>
                        <svg
                          className="mx-auto h-12 w-12 text-gray-400"
                          stroke="currentColor"
                          fill="none"
                          viewBox="0 0 48 48"
                          aria-hidden="true"
                        >
                          <path
                            d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                            strokeWidth={2}
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                        <div className="flex text-sm text-gray-600">
                          <label
                            htmlFor="decisionFile"
                            className="relative cursor-pointer bg-white rounded-md font-medium text-[#F55D3E] hover:text-[#e04d30] focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-[#F55D3E]"
                          >
                            <span>Upload a file</span>
                            <input
                              id="decisionFile"
                              name="decisionFile"
                              type="file"
                              className="sr-only"
                              accept="application/pdf"
                              onChange={handleDecisionFileChange}
                              required
                            />
                          </label>
                          <p className="pl-1">or drag and drop</p>
                        </div>
                        <p className="text-xs text-gray-500">PDF up to 10MB</p>
                      </>
                    ) : (
                      <div className="flex flex-col items-center">
                        <svg
                          className="h-10 w-10 text-green-500"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                          />
                        </svg>
                        <p className="mt-2 text-sm text-gray-900 font-medium">
                          {decisionFile.name}
                        </p>
                        <p className="mt-1 text-xs text-gray-500">
                          {(decisionFile.size / 1024 / 1024).toFixed(2)} MB
                        </p>
                        <button
                          type="button"
                          onClick={handleRemoveDecisionFile}
                          className="mt-2 inline-flex items-center px-2.5 py-1.5 border border-transparent text-xs font-medium rounded text-red-700 bg-red-100 hover:bg-red-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                        >
                          Remove file
                        </button>
                      </div>
                    )}
                  </div>
                </div>
                {decisionFileError && (
                  <p className="mt-1 text-sm text-red-600">{decisionFileError}</p>
                )}
              </div>
              
              <div className="flex justify-end">
                <button
                  type="submit"
                  disabled={
                    !decision || 
                    decisionData.trim().length < 500 || 
                    !decisionFile ||
                    decisionSubmitStatus === 'submitting'
                  }
                  className={`px-4 py-2 rounded text-white ${
                    !decision || decisionData.trim().length < 500 || !decisionFile || decisionSubmitStatus === 'submitting'
                      ? 'bg-gray-300 cursor-not-allowed'
                      : 'bg-[#F55D3E] hover:bg-[#e04d30] transition-colors'
                  }`}
                >
                  {decisionSubmitStatus === 'submitting' ? (
                    <span className="flex items-center">
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      {fileUploading ? 'Uploading file...' : 'Submitting...'}
                    </span>
                  ) : (
                    'Submit Decision'
                  )}
                </button>
              </div>
            </div>
          </form>
        </div>
        
      </div>
    </>
  );
};

export default withIdleTimer(AdminReview);
